import { ReactNode, useMemo } from 'react';
import { format, isToday, isTomorrow, isYesterday, type Locale } from 'date-fns';
import { cn } from '@/lib/utils';

/*
 * ChronoList — chronological list with sticky date headers.
 *
 * Generic shell extracted from the duplicate MobileListView and
 * MobileBookingListView surfaces. Pass items, a date accessor, and a
 * per-item renderer; ChronoList groups by yyyy-MM-dd, applies friendly
 * date labels (Today / Tomorrow / Yesterday / formatted), and renders a
 * sticky header per group.
 */

interface ChronoListProps<T> {
  items: T[];
  /** Extract the Date used for grouping. */
  dateAccessor: (item: T) => Date;
  renderItem: (item: T, index: number) => ReactNode;
  /** Item key for React reconciliation. Default: index. */
  itemKey?: (item: T, index: number) => string | number;
  /** Custom date label (e.g. localized). Default: Today/Tomorrow/Yesterday/d MMM. */
  formatDate?: (d: Date) => string;
  locale?: Locale;
  /** Rendered when items.length === 0. */
  emptyState?: ReactNode;
  className?: string;
}

export function ChronoList<T>({
  items,
  dateAccessor,
  renderItem,
  itemKey,
  formatDate,
  locale,
  emptyState,
  className,
}: ChronoListProps<T>) {
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; date: Date; items: { item: T; index: number }[] }>();
    items.forEach((item, index) => {
      const d = dateAccessor(item);
      const key = format(d, 'yyyy-MM-dd');
      if (!map.has(key)) {
        map.set(key, {
          label: formatDate ? formatDate(d) : defaultLabel(d, locale),
          date: d,
          items: [],
        });
      }
      map.get(key)!.items.push({ item, index });
    });
    // Sort groups chronologically.
    return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [items, dateAccessor, formatDate, locale]);

  if (items.length === 0 && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {groups.map((group) => (
        <section key={group.date.toISOString()}>
          <header
            className="sticky top-0 z-10 bg-bg-app/95 backdrop-blur-sm px-4 py-2 text-mono-eyebrow text-soft border-b border-line"
          >
            {group.label}
          </header>
          <ul className="flex flex-col">
            {group.items.map(({ item, index }) => (
              <li key={itemKey ? itemKey(item, index) : index}>
                {renderItem(item, index)}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function defaultLabel(d: Date, locale?: Locale): string {
  const opts = locale ? { locale } : undefined;
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'EEEE, d MMM', opts);
}
